export enum ChannelStatus {
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  DISCONNECTED = "DISCONNECTED",
  FAILED = "FAILED",
};

export interface Channel {
  id: string;
  organizationId?: string;
  name?: string;
  status: ChannelStatus;
  isDefault?: boolean;
  connectedJid?: string;
  connectedPhone?: string;
  qrCode?: string;
  updatedAt: Date;
};

export type ConnectedPhoneDisplay = {
  value: string;
  secondary?: string;
  identified: boolean;
};

export type ChannelListResponse = Array<Channel> | Channel | {
  results?: Array<Channel>;
  data?: Array<Channel> | Channel | { results?: Array<Channel> };
};

export const normalizeChannelStatusValue = (value?: string | null): ChannelStatus | null => {
  switch ((value || "").trim().toUpperCase()) {
    case ChannelStatus.CONNECTED:
      return ChannelStatus.CONNECTED;
    case ChannelStatus.CONNECTING:
    case "AWAITING_QR":
      return ChannelStatus.CONNECTING;
    case ChannelStatus.FAILED:
      return ChannelStatus.FAILED;
    case ChannelStatus.DISCONNECTED:
    case "LOGGED_OUT":
      return ChannelStatus.DISCONNECTED;
    default:
      return null;
  }
};

export const normalizeChannel = (channel: Channel): Channel => ({
  ...channel,
  status: normalizeChannelStatusValue(channel.status) ?? channel.status ?? ChannelStatus.DISCONNECTED,
  updatedAt: channel.updatedAt ? new Date(channel.updatedAt) : new Date(),
});

export const formatConnectedPhone = (phone?: string | null): string => {
  const trimmed = (phone || "").trim();
  if( !trimmed ) return "Não identificado";

  const digits = trimmed.replace(/\D/g, "");
  if( digits === trimmed && digits.startsWith("55") ) {
    const areaCode = digits.slice(2, 4);
    const localNumber = digits.slice(4);
    if( areaCode.length === 2 && localNumber.length === 8 ) {
      return `+55 ${ areaCode } ${ localNumber.slice(0, 4) }-${ localNumber.slice(4) }`;
    }
    if( areaCode.length === 2 && localNumber.length === 9 ) {
      return `+55 ${ areaCode } ${ localNumber.slice(0, 5) }-${ localNumber.slice(5) }`;
    }
  }

  return trimmed;
};

export const resolveConnectedPhoneDisplay = (channel: Pick<Channel, "connectedPhone" | "status">): ConnectedPhoneDisplay => {
  const normalizedPhone = channel.connectedPhone?.trim();
  if( !normalizedPhone ) {
    return { value: "Não identificado", identified: false };
  }

  return {
    value: formatConnectedPhone(normalizedPhone),
    secondary: channel.status === ChannelStatus.CONNECTED ? undefined : "Último número",
    identified: true,
  };
};

export const normalizeChannels = (value: ChannelListResponse | null | undefined): Array<Channel> => {
  if( Array.isArray(value) ) return value.map(normalizeChannel);
  if( !value || typeof value !== "object" ) return [];
  if( "results" in value && Array.isArray(value.results) ) return value.results.map(normalizeChannel);
  if( "data" in value ) return normalizeChannels(value.data as ChannelListResponse);
  if( "id" in value && typeof value.id === "string" ) return [ normalizeChannel(value as Channel) ];

  return [];
};

export const mergeChannelFallback = (items: Array<Channel>, fallback?: Channel | null): Array<Channel> => {
  const normalizedItems = normalizeChannels(items);
  if( !fallback?.id ) return normalizedItems;

  const fallbackChannel: Channel = normalizeChannel({
    ...fallback,
    name: fallback.name || "Canal principal",
    isDefault: fallback.isDefault ?? true,
  });

  if( normalizedItems.some((channel) => channel.id === fallbackChannel.id) ) {
    return normalizedItems.map((channel) => channel.id === fallbackChannel.id ? { ...fallbackChannel, ...channel } : channel);
  }

  return [ fallbackChannel, ...normalizedItems ];
};

export type ChannelConnectionListResponse = ChannelListResponse;

export const normalizeChannelConnection = (connection: Channel): Channel => ({
  ...connection,
  status: normalizeChannelStatusValue(connection.status) ?? connection.status ?? ChannelStatus.DISCONNECTED,
  updatedAt: connection.updatedAt ? new Date(connection.updatedAt) : new Date(),
});

export const normalizeChannelConnections = (value: ChannelListResponse | null | undefined): Array<Channel> => {
  if( Array.isArray(value) ) return value.map(normalizeChannelConnection);
  if( !value || typeof value !== "object" ) return [];
  if( "results" in value && Array.isArray(value.results) ) return value.results.map(normalizeChannelConnection);
  if( "data" in value ) return normalizeChannelConnections(value.data as ChannelListResponse);
  if( "id" in value && typeof value.id === "string" ) return [ normalizeChannelConnection(value as Channel) ];

  return [];
};

export const mergeChannelConnectionFallback = (items: Array<Channel>, fallback?: Channel | null): Array<Channel> => {
  const normalizedItems = normalizeChannelConnections(items);
  if( !fallback?.id ) return normalizedItems;

  const fallbackConnection: Channel = normalizeChannelConnection({
    ...fallback,
    name: fallback.name || "Canal principal",
    isDefault: fallback.isDefault ?? true,
  });

  if( normalizedItems.some((connection) => connection.id === fallbackConnection.id) ) {
    return normalizedItems.map((connection) => connection.id === fallbackConnection.id ? { ...fallbackConnection, ...connection } : connection);
  }

  return [ fallbackConnection, ...normalizedItems ];
};
